import { useEffect, useRef, useState } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import type { Session } from "@supabase/supabase-js";
import { supabase } from "./lib/supabase";
import { emitToast, ToastBridge, ToastProvider } from "./components/Toast";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { CandidateApp } from "./candidate/CandidateApp";
import { Login } from "./reviewer/Login";
import { ReviewerApp } from "./reviewer/ReviewerApp";

export default function App() {
  return (
    <ToastProvider>
      <ToastBridge />
      <ErrorBoundary>
        <AppInner />
      </ErrorBoundary>
    </ToastProvider>
  );
}

function Splash() {
  return <div className="splash">Carregando…</div>;
}

function AppInner() {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const bootRetried = useRef(false);

  useEffect(() => {
    (async () => {
      const { data, error } = await supabase.auth.getSession();
      if (error || !data.session) {
        setSession(null);
        setLoading(false);
        return;
      }
      const { error: userErr } = await supabase.auth.getUser();
      if (userErr && !bootRetried.current) {
        bootRetried.current = true;
        const { error: refreshErr } = await supabase.auth.refreshSession();
        if (refreshErr) {
          await supabase.auth.signOut({ scope: "global" });
          emitToast("warning", "Sessão expirou. Faça login novamente.");
          setSession(null);
          setLoading(false);
          return;
        }
        const { data: data2 } = await supabase.auth.getSession();
        setSession(data2.session);
      } else {
        setSession(data.session);
      }
      setLoading(false);
    })();

    const { data: sub } = supabase.auth.onAuthStateChange((_evt, s) => {
      setSession(s);
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  return (
    <Routes>
      {/* Candidato — público, autenticado por token de convite (não usa sessão Supabase) */}
      <Route path="/t/:token" element={<CandidateApp />} />

      {/* Revisor — gated por sessão Google @reachup.pro */}
      <Route
        path="/login"
        element={loading ? <Splash /> : session ? <Navigate to="/admin" replace /> : <Login />}
      />
      <Route
        path="/admin/*"
        element={
          loading ? <Splash /> : session ? <ReviewerApp session={session} /> : <Navigate to="/login" replace />
        }
      />
      <Route path="/" element={<Navigate to="/admin" replace />} />
      <Route path="*" element={<Navigate to="/admin" replace />} />
    </Routes>
  );
}
